<template>
  <div>
    <!-- Filters -->
    <b-card no-body>
      <b-card-header class="pb-50">
        <h5 class="mb-1">
          Filtros
        </h5>
      </b-card-header>
      <b-card-body>
        <b-row>
          <b-col
            cols="12"
            md="3"
            class="mb-1"
          >
            <b-form-group
              label="Período"
              label-for="searchPeriod"
            >
              <flat-pickr
                id="searchPeriod"
                v-model="period"
                class="form-control"
                :config="flatPickrConfig"
                placeholder="Selecione o período"
              />
            </b-form-group>
          </b-col>
          <b-col
            cols="12"
            md="3"
            class="mb-1"
          >
            <b-form-group
              label="Competência"
              label-for="searchCompetence"
            >
              <b-form-input
                id="searchCompetence"
                v-model="competence"
                type="month"
              />
            </b-form-group>
          </b-col>
          <b-col
            cols="12"
            md="3"
            class="mb-1"
          >
            <b-form-group
              label="Usuário"
              label-for="searchUser"
            >
              <b-form-input
                id="searchUser"
                v-model="user"
                placeholder="Nome ou e-mail do usuário"
                @keyup.enter="search(1)"
              />
            </b-form-group>
          </b-col>
          <b-col
            cols="12"
            md="3"
            class="mb-1"
          >
            <b-form-group
              label="Categoria"
              label-for="searchCategory"
            >
              <v-select
                id="searchCategory"
                v-model="category"
                :reduce="option => option.value"
                :options="categories"
                :loading="loading.categories"
                label="label"
              >
                <template #no-options>
                  Sem resultados
                </template>
              </v-select>
            </b-form-group>
          </b-col>
          <b-col
            cols="12"
            md="3"
            class="mb-1"
          >
            <b-form-group
              label="Contrato"
              label-for="searchContract"
            >
              <b-form-input
                id="searchContract"
                v-model="contractId"
                type="number"
                @keyup.enter="search(1)"
              />
            </b-form-group>
          </b-col>
          <b-col
            cols="12"
            class="mb-1 text-right"
          >
            <b-button
              variant="outline-secondary"
              class="mr-1"
              :disabled="loading.search"
              @click.prevent="clearSearch"
            >
              Limpar
            </b-button>
            <b-button
              variant="outline-primary"
              :disabled="loading.search"
              @click.prevent="search(1)"
            >
              <b-spinner
                v-if="loading.search"
                small
              />
              <feather-icon
                v-else
                icon="SearchIcon"
                class="mr-50"
              />
              <span>
                {{ loading.search ? "Aguarde..." : "Pesquisar" }}
              </span>
            </b-button>
          </b-col>
        </b-row>
      </b-card-body>
    </b-card>

    <!-- Table Container Card -->
    <b-card
      no-body
      class="mb-0"
    >
      <div class="m-2">
        <!-- Table Top -->
        <b-row>
          <!-- Per Page -->
          <b-col
            cols="12"
            md="6"
            class="d-flex align-items-center justify-content-start mb-1 mb-md-0"
          >
            <label class="mb-0">Exibir</label>
            <v-select
              v-model="itemsPerPage"
              dir="ltr"
              :options="itemsPerPageOptions"
              :clearable="false"
              class="per-page-selector d-inline-block mx-50"
              @input="search(currentPage)"
            >
              <template #no-options>
                Sem resultados
              </template>
            </v-select>
            <label class="mb-0">itens</label>
          </b-col>
          <!-- Export -->
          <b-col
            cols="12"
            md="6"
          >
            <b-row class="d-flex align-items-center justify-content-end">
              <div class="mx-1">
                <b-button
                  variant="outline-primary"
                  :disabled="loading.export"
                  @click.prevent="exportData"
                >
                  <b-spinner
                    v-if="loading.export"
                    small
                  />
                  <feather-icon
                    v-else
                    icon="ArrowDownCircleIcon"
                    class="mr-50"
                  />
                  <span>
                    {{ loading.export ? "Aguarde..." : "Exportar planilha" }}
                  </span>
                </b-button>
              </div>
            </b-row>
          </b-col>
        </b-row>
      </div>
      <b-table
        :items="items"
        responsive
        :fields="tableColumns"
        primary-key="id"
        show-empty
        empty-text="Não foram encontradas alterações extraordinárias para esta busca"
        :busy="loading.search"
      >
        <!-- Column: data/hora -->
        <template #cell(created_at)="data">
          <div class="text-wrap">
            <span class="align-text-top">{{ data.item.created_at | dateTime }}</span>
          </div>
        </template>
        <!-- Column: competência -->
        <template #cell(competence)="data">
          <div class="text-wrap">
            <span class="align-text-top">{{ formatCompetence(data.item.competence) }}</span>
          </div>
        </template>
        <!-- Column: contrato -->
        <template #cell(contract_id)="data">
          <div class="text-wrap">
            <router-link
              :to="{
                name: 'contract-details-summary',
                params: { id: data.item.contract_id },
              }"
              target="_blank"
            >
              {{ data.item.contract_id }}
            </router-link>
          </div>
        </template>
        <!-- Column: usuário -->
        <template #cell(user)="data">
          <div class="text-wrap">
            <span class="align-text-top">{{ userName(data.item) }}</span>
          </div>
        </template>
        <!-- Column: perfil -->
        <template #cell(role)="data">
          <div class="text-wrap">
            <span class="align-text-top">{{ roleName(data.item) }}</span>
          </div>
        </template>
        <!-- Column: categoria -->
        <template #cell(category)="data">
          <div class="text-wrap">
            <span class="align-text-top">{{ categoryLabel(data.item) }}</span>
          </div>
        </template>
        <!-- Column: chamado -->
        <template #cell(ticket_number)="data">
          <div class="text-wrap">
            <span class="align-text-top">{{ data.item.ticket_number || "Não informado" }}</span>
          </div>
        </template>
        <!-- Column: campos alterados -->
        <template #cell(changes)="data">
          <div class="text-wrap">
            <template v-if="changesOf(data.item).length">
              <span
                :id="`changes-pop-${data.item.id}`"
                class="align-text-top cursor-pointer text-primary"
              >{{ truncate(changesSummary(data.item)) }}</span>
              <b-popover
                :target="`changes-pop-${data.item.id}`"
                triggers="hover"
                placement="top"
                boundary="window"
              >
                <template #title>
                  Campos alterados
                </template>
                <div
                  v-for="(change, index) in changesOf(data.item)"
                  :key="index"
                  class="mb-25"
                >
                  <b>{{ changeFieldLabel(change) }}:</b>
                  {{ changeValue(change.from) }} → {{ changeValue(change.to) }}
                </div>
              </b-popover>
            </template>
            <span
              v-else
              class="align-text-top text-muted"
            >Não informados</span>
          </div>
        </template>
        <!-- Column: motivo -->
        <template #cell(reason)="data">
          <div class="text-wrap">
            <span
              :id="`reason-pop-${data.item.id}`"
              class="align-text-top"
            >{{ truncate(data.item.reason) }}</span>
            <b-popover
              v-if="isTruncated(data.item.reason)"
              :target="`reason-pop-${data.item.id}`"
              triggers="hover"
              placement="top"
              boundary="window"
            >
              {{ data.item.reason }}
            </b-popover>
          </div>
        </template>
        <!-- Column: impacto -->
        <template #cell(expected_impact)="data">
          <div class="text-wrap">
            <b-badge
              v-for="impact in impactsOf(data.item)"
              :key="impact"
              variant="light-warning"
              class="mr-25"
            >
              {{ impactText(impact) }}
            </b-badge>
          </div>
        </template>
        <!-- Column: status da revisão -->
        <template #cell(review_status)="data">
          <b-badge :variant="reviewStatusVariant(data.item)">
            {{ reviewStatusLabel(data.item) }}
          </b-badge>
        </template>
      </b-table>
      <div class="mx-2 mb-2">
        <b-row>
          <b-col
            cols="12"
            sm="6"
            class="d-flex align-items-center justify-content-center justify-content-sm-start"
          >
            <span class="text-muted">
              Exibindo {{ pageItemsFrom }} à {{ pageItemsTo }} de {{ totalItems }} itens
            </span>
          </b-col>
          <!-- Pagination -->
          <b-col
            cols="12"
            sm="6"
            class="d-flex align-items-center justify-content-center justify-content-sm-end"
          >
            <b-pagination
              :value="currentPage"
              :total-rows="totalItems"
              :per-page="itemsPerPage"
              first-number
              last-number
              class="mb-0 mt-1 mt-sm-0"
              prev-class="prev-item"
              next-class="next-item"
              @change="search"
            >
              <template #prev-text>
                <feather-icon
                  icon="ChevronLeftIcon"
                  size="18"
                />
              </template>
              <template #next-text>
                <feather-icon
                  icon="ChevronRightIcon"
                  size="18"
                />
              </template>
            </b-pagination>
          </b-col>
        </b-row>
      </div>
    </b-card>
  </div>
</template>

<script>
import {
  BCard,
  BCardHeader,
  BCardBody,
  BRow,
  BCol,
  BFormGroup,
  BFormInput,
  BButton,
  BTable,
  BBadge,
  BPopover,
  BPagination,
  BSpinner,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import flatPickr from 'vue-flatpickr-component'
// flatpickr entra como dependência transitiva de vue-flatpickr-component
// eslint-disable-next-line import/no-extraneous-dependencies
import { Portuguese } from 'flatpickr/dist/l10n/pt'
import { mapActions, mapGetters } from 'vuex'
import { useToast } from 'vue-toastification/composition'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import { apiErrorToast } from '@/helpers/error_messages'
import { getRangeDates } from '@/helpers/date_picker'
import {
  impactLabel,
  REVIEW_STATUS_LABELS,
  REVIEW_STATUS_VARIANTS,
} from '@/constants/contract_closing'
import { competenceLabel } from '@/modules/contract/utils/contractLock'
import * as sharedTypes from '@/modules/shared/store/types'
import * as types from '../store/types'

const MAX_CELL_LENGTH = 40

export default {
  components: {
    BCard,
    BCardHeader,
    BCardBody,
    BRow,
    BCol,
    BFormGroup,
    BFormInput,
    BButton,
    BTable,
    BBadge,
    BPopover,
    BPagination,
    BSpinner,
    vSelect,
    flatPickr,
  },
  setup() {
    return { toast: useToast() }
  },
  data() {
    return {
      loading: {
        search: false,
        export: false,
        categories: false,
      },
      flatPickrConfig: {
        altFormat: 'j M Y',
        altInput: true,
        mode: 'range',
        dateFormat: 'Y-m-d',
        locale: Portuguese,
      },
      tableColumns: [
        { key: 'created_at', label: 'Data/hora' },
        { key: 'competence', label: 'Competência' },
        { key: 'contract_id', label: 'Contrato' },
        { key: 'user', label: 'Usuário' },
        { key: 'role', label: 'Perfil' },
        { key: 'category', label: 'Categoria' },
        { key: 'ticket_number', label: 'Chamado' },
        { key: 'changes', label: 'Campos alterados' },
        { key: 'reason', label: 'Motivo' },
        { key: 'expected_impact', label: 'Impacto' },
        { key: 'review_status', label: 'Status da revisão' },
      ],
      currentPage: 1,
      itemsPerPage: 1,
      period: null,
      competence: null,
      user: null,
      category: null,
      contractId: null,
    }
  },
  computed: {
    ...mapGetters({
      searchResult: types.CLOSING_EXCEPTIONS_SEARCH_RESULT,
      categories: types.CLOSING_EXCEPTION_CATEGORIES,
      initialItemsPerPage: sharedTypes.INITIAL_ITEMS_PER_PAGE,
      itemsPerPageOptions: sharedTypes.ITEMS_PER_PAGE_OPTIONS,
    }),
    items() {
      return this.searchResult?.data || []
    },
    totalItems() {
      return this.searchResult?.total || 0
    },
    pageItemsFrom() {
      return this.searchResult?.from || 0
    },
    pageItemsTo() {
      return this.searchResult?.to || 0
    },
  },
  mounted() {
    this.itemsPerPage = this.initialItemsPerPage
    // Pré-filtro vindo do ledger de competências.
    if (this.$route.query.competence) {
      this.competence = this.$route.query.competence
    }
    this.loading.categories = true
    this.getCategories()
      .catch(error => {
        this.toast({
          component: ToastificationContent,
          props: apiErrorToast(
            error,
            'Ocorreu um erro ao carregar as categorias para seleção. Entre em contato com o setor de TI.',
          ),
        })
      })
      .finally(() => {
        this.loading.categories = false
      })
    this.search()
  },
  methods: {
    ...mapActions({
      searchExceptions: types.SEARCH_CLOSING_EXCEPTIONS,
      exportExceptions: types.EXPORT_CLOSING_EXCEPTIONS,
      getCategories: types.GET_CLOSING_EXCEPTION_CATEGORIES,
    }),
    formatCompetence(competence) {
      return competenceLabel(competence) || 'Não informada'
    },
    userName(item) {
      if (typeof item.user === 'string' && item.user) return item.user

      return item.user?.name || item.user_name || 'Não identificado'
    },
    roleName(item) {
      return item.user?.role_name || item.user_role || item.role_name || 'Não informado'
    },
    categoryLabel(item) {
      if (item.category_label) return item.category_label
      const option = (this.categories || []).find(entry => entry.value === item.category)

      return option ? option.label : item.category || 'Não informada'
    },
    changesOf(item) {
      return Array.isArray(item.changes) ? item.changes : []
    },
    changeFieldLabel(change) {
      return change.field_label || change.field || 'Campo'
    },
    changeValue(value) {
      if (value === null || value === undefined || value === '') return 'vazio'

      return String(value)
    },
    changesSummary(item) {
      return this.changesOf(item)
        .map(change => `${this.changeFieldLabel(change)}: ${this.changeValue(change.from)} → ${this.changeValue(change.to)}`)
        .join(' · ')
    },
    isTruncated(value) {
      return typeof value === 'string' && value.length > MAX_CELL_LENGTH
    },
    truncate(value) {
      if (!value) return 'Não informado'
      if (!this.isTruncated(value)) return value

      return `${value.slice(0, MAX_CELL_LENGTH)}...`
    },
    impactsOf(item) {
      if (Array.isArray(item.expected_impact)) return item.expected_impact
      if (typeof item.expected_impact === 'string' && item.expected_impact) {
        return item.expected_impact.split(',').map(impact => impact.trim())
      }

      return []
    },
    impactText(impact) {
      return impactLabel(impact)
    },
    reviewStatusLabel(item) {
      return item.review_status_label
        || REVIEW_STATUS_LABELS[item.review_status]
        || 'Pendente'
    },
    reviewStatusVariant(item) {
      return REVIEW_STATUS_VARIANTS[item.review_status] || 'light-warning'
    },
    filters() {
      const dates = getRangeDates(this.period)

      return {
        initialDate: dates.start,
        endDate: dates.end,
        competence: this.competence,
        user: this.user,
        category: this.category,
        contractId: this.contractId,
      }
    },
    search(currentPage) {
      this.loading.search = true
      this.currentPage = Number.isInteger(currentPage) ? currentPage : 1
      this.searchExceptions({
        ...this.filters(),
        limit: this.itemsPerPage,
        currentPage: this.currentPage,
      })
        .catch(error => {
          this.toast({
            component: ToastificationContent,
            props: apiErrorToast(
              error,
              'Ocorreu um erro ao buscar as alterações extraordinárias. Entre em contato com o setor de TI.',
            ),
          })
        })
        .finally(() => {
          this.loading.search = false
        })
    },
    exportData() {
      this.loading.export = true
      this.exportExceptions(this.filters())
        .catch(error => {
          this.toast({
            component: ToastificationContent,
            props: apiErrorToast(
              error,
              'Ocorreu um erro ao exportar as alterações extraordinárias. Entre em contato com o setor de TI.',
            ),
          })
        })
        .finally(() => {
          this.loading.export = false
        })
    },
    clearSearch() {
      this.period = null
      this.competence = null
      this.user = null
      this.category = null
      this.contractId = null
      this.search(1)
    },
  },
}
</script>

<style lang="scss" scoped>
.per-page-selector {
  width: 90px;
}
</style>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";
@import "@core/scss/vue/libs/vue-flatpicker.scss";
</style>
